import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardHeader,
    TextField,
    Button,
    Alert,
    CircularProgress,
    useTheme as useMuiTheme,
    useMediaQuery,
    InputAdornment
} from '@mui/material';
import {
    Save as SaveIcon,
    Tune as TuneIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchSettings, updateSettings } from '../../store/slices/settingsSlice';
import { useTheme } from '../../contexts/ThemeContext';
import Toast from '../../components/Toast';
import useToast from '../../hooks/useToast';

const GeneralSettings = () => {
    const muiTheme = useMuiTheme();
    const { theme: appTheme } = useTheme();
    const dispatch = useAppDispatch();
    const { settings, loading, error } = useAppSelector((state) => state.settings);
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const { toast, showSuccess, showError, hideToast } = useToast();

    const [gst, setGst] = useState<string>('');
    const [formError, setFormError] = useState<string>('');

    useEffect(() => {
        dispatch(fetchSettings());
    }, [dispatch]);

    useEffect(() => {
        if (settings) {
            setGst(settings.gst?.toString() || '');
        }
    }, [settings]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError('');

        // Validation
        const gstValue = parseFloat(gst);
        if (isNaN(gstValue) || gstValue < 0 || gstValue > 100) {
            setFormError('GST must be a number between 0 and 100');
            return;
        }

        try {
            await dispatch(updateSettings({ gst: gstValue })).unwrap();
            showSuccess('Settings updated successfully');
        } catch (err) {
            showError(err instanceof Error ? err.message : 'Failed to update settings');
        }
    };

    if (loading && !settings) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: isMobile ? 2 : 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant={isMobile ? "h5" : "h4"}
                    sx={{
                        color: appTheme.palette.text.primary,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <TuneIcon />
                    General Settings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Configure system-wide settings
                </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Settings Form */}
            <Card sx={{
                maxWidth: 600,
                bgcolor: muiTheme.palette.background.paper,
                border: `1px solid ${muiTheme.palette.divider}`
            }}>
                <CardHeader
                    title="Tax Settings"
                    sx={{
                        bgcolor: muiTheme.palette.action.hover,
                        '& .MuiCardHeader-title': {
                            fontWeight: 600,
                            fontSize: '1.1rem'
                        }
                    }}
                />
                <CardContent>
                    <form onSubmit={handleSubmit}>
                        {formError && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {formError}
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            label="GST (%)"
                            type="number"
                            value={gst}
                            onChange={(e) => setGst(e.target.value)}
                            required
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                inputProps: { min: 0, max: 100, step: 0.01 }
                            }}
                            helperText="Enter the GST percentage to be applied on consignments"
                            sx={{ mb: 3 }}
                        />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                disabled={loading}
                                sx={{
                                    bgcolor: appTheme.palette.secondary.main,
                                    '&:hover': {
                                        bgcolor: appTheme.palette.secondary.dark,
                                    }
                                }}
                            >
                                {loading ? 'Saving...' : 'Save Settings'}
                            </Button>
                        </Box>
                    </form>
                </CardContent>
            </Card>

            {/* Toast Notification */}
            <Toast
                open={toast.open}
                message={toast.message}
                title={toast.title}
                severity={toast.severity}
                onClose={hideToast}
            />
        </Box>
    );
};

export default GeneralSettings;
